import { AccommodationProps } from "@/components/Accommodation/AccommodationProps.ts";
import { AccommodationRatePicker } from "@/components/AccommodationRatePicker/AccommodationRatePicker.tsx";
import { AmenityTag } from "@/components/AmenityTag/AmenityTag.tsx";
import Gallery from "@/components/Gallery/Gallery.tsx";
import { PriceAndCapacity } from "@/components/PriceAndCapacity/PriceAndCapacity.tsx";
import { useGetLowestPriceFrom } from "@/helpers/getLowestPriceFrom.ts";
import { mergeRatePlans } from "@/helpers/mergeRatePlans.ts";
import { useGetAmenityTranslated } from "@/helpers/translation/useGetAmenityTranslated.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";

import { handleGTMPreviewAccommodationClick } from "@/helpers/gtm/handleGTMPreviewAccommodationClick.ts";
import { CalendarX2, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const Accommodation = (props: AccommodationProps) => {
  const { t } = useTranslation();
  const rg = props.rateGroup;
  const areAllRPsUnavailable = mergeRatePlans(rg).every((rp) => !rp.isAvailable);
  const { cartAccommodationItems } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
    })),
  );
  const cartCount = cartAccommodationItems.filter((item) => item.rateGroup.id === rg.id).length;
  const { name } = useGetRGTranslated(rg);
  const amenities = useGetAmenityTranslated(rg?.amenities);
  const lowestPriceFrom = useGetLowestPriceFrom(rg);

  // todo: uncomment when ready
  // const guestCapacity = rg.guestAdditionalCapacity
  //   ? `${rg.guestCapacity}+${rg.guestAdditionalCapacity}`
  //   : rg.guestCapacity;
  const guestCapacity = rg.guestCapacity;

  const shouldShowRates = props.showRateSwitcher && !areAllRPsUnavailable;

  const handleOpenPreviewModal = () => {
    props.handleOpenPreviewModal(rg);
    handleGTMPreviewAccommodationClick(rg);
  };

  const sortedImages = useMemo(() => {
    const sortedPerGalleryPositionIndex = [...(rg.images ?? [])].sort((a, b) => {
      return (a.galleryPositionIndex ?? 0) - (b.galleryPositionIndex ?? 0);
    });

    const primaryImage = sortedPerGalleryPositionIndex.find((image) => image.isPrimary);
    if (primaryImage) {
      const filteredImages = sortedPerGalleryPositionIndex.filter((image) => !image.isPrimary);
      return [primaryImage, ...filteredImages];
    }
    return sortedPerGalleryPositionIndex;
  }, [rg.images]);

  const galleryBlock = (
    <div
      className="accommodationImages c-accommodation__gallery"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest(".c-gallery__button, .embla__dot")) return;
        handleOpenPreviewModal();
      }}
    >
      <Gallery
        disabled={areAllRPsUnavailable}
        slides={sortedImages}
        options={{ loop: true }}
        thumbnails={false}
      />
      {areAllRPsUnavailable && (
        <div className="c-accommodation__gallery-unavailable-overlay">
          <div className="c-accommodation__gallery-unavailable-overlay-icon">
            <CalendarX2 size={28} strokeWidth={1.5} />
          </div>
          <span className="c-accommodation__gallery-unavailable-overlay-title">{t("unavailable")}</span>
          <span className="c-accommodation__gallery-unavailable-overlay-subtitle">
            {t("forSelectedDates")}
          </span>
        </div>
      )}
    </div>
  );

  const infoBlock = (
    <div
      className={`accommodationInfoWrap c-accommodation__info${areAllRPsUnavailable ? "--unavailable" : ""}`}
    >
      <div className="c-accommodation__info-content">
        <h3 className="u-h5 u-fw-600 c-accommodation__info-title">{name}</h3>
        <PriceAndCapacity
          priceFrom={lowestPriceFrom}
          capacity={guestCapacity}
          additionalCapacity={rg.guestAdditionalCapacity}
        />
        {amenities?.length > 0 && (
          <div className="c-accommodation__amenities">
            {amenities.map((amenity, i) => (
              <AmenityTag key={i} name={amenity} iconSize={12} />
            ))}
          </div>
        )}
      </div>
      <button type="button" className="c-accommodation__details-link" onClick={handleOpenPreviewModal}>
        <span>{t("details")}</span>
        <ChevronRight size={14} />
      </button>
    </div>
  );

  const cartLine = cartCount > 0 && (
    <div className="c-accommodation__cart-line">
      <span>{cartCount > 1 ? `${cartCount}x ${t("added")}` : t("added")}</span>
    </div>
  );

  if (shouldShowRates) {
    return (
      <div
        className={`accommodation c-accommodation c-accommodation--with-rates ${
          areAllRPsUnavailable ? "unavailableAcc" : undefined
        }`}
      >
        {cartLine}
        <div className="c-accommodation__top">
          {galleryBlock}
          {infoBlock}
        </div>
        <AccommodationRatePicker rateGroup={rg} />
      </div>
    );
  }

  return (
    <div className={`accommodation c-accommodation ${areAllRPsUnavailable ? "unavailableAcc" : undefined}`}>
      {cartLine}
      {galleryBlock}
      {infoBlock}
    </div>
  );
};
